import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Gift,
  Wallet as WalletIcon,
  CreditCard,
  Apple,
  Loader2,
  Copy,
  Check,
  Share2,
  MessageSquare,
  Car,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { NativeSelect } from "@/components/ui/native-select";
import { useToast } from "@/hooks/use-toast";
import { useServices } from "@/hooks/use-services";
import { useWalletBalance } from "@/hooks/use-wallet";
import { usePurchaseGift } from "@/hooks/use-gifts";
import type { Gift as GiftType } from "@/hooks/use-gifts";
import type { Service } from "@/lib/types";
import { cn, normalizeKuwaitPhoneInput } from "@/lib/utils";
import knetLogo from "@assets/knet-logo.png";
import creditCardLogos from "@assets/credit-card-logos.jpg";
import applePayLogoLight from "../assets/images/apple_pay_light.png";
import applePayLogoDark from "../assets/images/apple_pay_dark.png";
import { useTheme } from "@/components/ThemeProvider";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction } from "@/components/ui/alert-dialog";

const PRESET_AMOUNTS = [5, 10, 15, 20];

export default function Gifts() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { toast } = useToast();

  const [giftType, setGiftType] = useState<"value" | "package">("value");
  const [amount, setAmount] = useState<number>(5);
  const [customAmount, setCustomAmount] = useState<string>("");
  const [selectedServices, setSelectedServices] = useState<{ serviceId: string; vehicleType: string }[]>([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState<string>("");
  const [expandedService, setExpandedService] = useState<string | null>(null);
  const [recipientPhone, setRecipientPhone] = useState<string>("");
  const [recipientName, setRecipientName] = useState<string>("");
  const [deliveryMethod, setDeliveryMethod] = useState<"share" | "sms">("share");
  const [paymentMethod, setPaymentMethod] = useState<string>("wallet");
  const [showApplePayDialog, setShowApplePayDialog] = useState(false);
  const [copied, setCopied] = useState(false);
  const { theme } = useTheme();

  const { data: services = [], isLoading: servicesLoading } = useServices();
  const { data: walletData, isLoading: balanceLoading } = useWalletBalance();
  const walletBalance = walletData?.balance ?? 0;
  const currency = walletData?.currency ?? t("services.currency");

  const purchaseMutation = usePurchaseGift();

  // Use custom amount if entered, otherwise preset amount
  const effectiveAmount = customAmount ? parseFloat(customAmount) : amount;
  const selectedServiceObjects = selectedServices.map((sel) => {
    const svc = services.find((s) => s.id === sel.serviceId);
    if (!svc) return null;
    const vp = (svc.vehiclePricing || {}) as Record<string, number | undefined>;
    const price = vp[sel.vehicleType] ?? svc.price;
    return { ...svc, selectedPrice: price, vehicleType: sel.vehicleType };
  }).filter(Boolean) as (Service & { selectedPrice: number; vehicleType: string })[];

  const packageTotal = selectedServiceObjects.reduce((sum, s) => sum + s.selectedPrice, 0);
  const priceToPay = giftType === "value" ? effectiveAmount : packageTotal;
  const insufficientBalance = paymentMethod === "wallet" && priceToPay > walletBalance;

  const buildBody = () => {
    // Normalize recipient phone to full international format (+965XXXXXXXX)
    const recipientDigits = normalizeKuwaitPhoneInput(recipientPhone);
    const normalizedPhone = recipientDigits ? `+965${recipientDigits}` : undefined;
    const name = recipientName.trim() || undefined;
    if (giftType === "value") {
      return {
        type: "value" as const,
        value: effectiveAmount,
        recipientPhone: normalizedPhone,
        recipientName: name,
        deliveryMethod,
        paymentMethod: paymentMethod as any,
      };
    }
    return {
      type: "package" as const,
      serviceIds: selectedServices, // now includes { serviceId, vehicleType }
      recipientPhone: normalizedPhone,
      recipientName: name,
      deliveryMethod,
      paymentMethod: paymentMethod as any,
    };
  };

  const handlePurchase = () => {
    if (giftType === "package" && selectedServices.length === 0) return;
    if (insufficientBalance) return;
    // SMS delivery requires a recipient phone number.
    if (deliveryMethod === "sms" && !recipientPhone.trim()) {
      toast({
        title: t("giftPage.recipientPhoneRequired"),
        variant: "destructive",
      });
      return;
    }

    purchaseMutation.mutate(buildBody(), {
      onSuccess: (result) => {
        // Gateway payments: redirect to the payment link; code revealed after callback.
        if ("paymentRequired" in result && result.paymentRequired && result.paymentLink) {
          window.location.href = result.paymentLink;
          return;
        }
        // Wallet payment: gift is ready immediately.
        sessionStorage.setItem("giftResult", JSON.stringify(result));
        window.location.href = "/gift-success";
      },
      onError: (err: Error) => {
        toast({
          title: t("giftPage.purchaseFailed"),
          description: err.message,
          variant: "destructive",
        });
      },
    });
  };

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may be unavailable */
    }
  };

  const formatServiceName = (s: Service) =>
    isRtl ? s.nameAr || s.nameEn : s.nameEn || s.nameAr;


  return (
    <div className="min-h-screen pt-24 pb-24 bg-background" dir={isRtl ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4 max-w-2xl">
        <h1 className="text-2xl font-bold flex items-center gap-2 mb-6">
          <Gift className="w-6 h-6 text-primary" />
          {t("giftPage.title")}
        </h1>

        {/* Wallet balance display */}
        <div className="bg-primary text-primary-foreground rounded-2xl p-6 mb-6">
          <p className="text-sm opacity-90">{t("giftPage.walletBalance")}</p>
          {balanceLoading ? (
            <Skeleton className="h-10 w-40 mt-2 bg-primary-foreground/20" />
          ) : (
            <p className="text-4xl font-bold mt-1">
              {walletBalance.toFixed(3)}{" "}
              <span className="text-2xl font-medium opacity-90">{currency}</span>
            </p>
          )}
        </div>



        {/* Gift type selector */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button
            type="button"
            onClick={() => setGiftType("value")}
            className={`text-start rounded-2xl border p-4 transition-colors ${
              giftType === "value"
                ? "border-primary bg-primary/5"
                : "border-border/60 bg-card hover:border-primary/40"
            }`}
          >
            <Gift className="w-5 h-5 text-primary mb-2" />
            <p className="font-semibold">{t("giftPage.fixedValue")}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t("giftPage.fixedValueDesc")}
            </p>
          </button>
          <button
            type="button"
            onClick={() => setGiftType("package")}
            className={`text-start rounded-2xl border p-4 transition-colors ${
              giftType === "package"
                ? "border-primary bg-primary/5"
                : "border-border/60 bg-card hover:border-primary/40"
            }`}
          >
            <Gift className="w-5 h-5 text-primary mb-2" />
            <p className="font-semibold">{t("giftPage.package")}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t("giftPage.packageDesc")}
            </p>
          </button>
        </div>

        {/* Conditional inputs */}
        {giftType === "value" ? (
          <Card className="rounded-xl border border-border/50 p-6 mb-6">
            <Label className="mb-3 block">{t("giftPage.amount")}</Label>
            <Input
              type="text"
              inputMode="decimal"
              value={customAmount}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9.]/g, "");
                setCustomAmount(val);
              }}
              placeholder={t("giftPage.customAmountPlaceholder")}
              className="h-12 rounded-xl"
              dir="ltr"
            />
          </Card>
        ) : (
          <>
            {/* Step 1: Vehicle type */}
            <Card className="rounded-xl border border-border/50 p-6 mb-6">
              <Label className="mb-3 block flex items-center gap-1">
                <Car className="h-4 w-4" /> {t("giftPage.selectVehicle")}
              </Label>
              <div className="flex flex-wrap gap-2">
                {services.filter(s => !s.isAddOn).flatMap(s => {
                  const vp = (s.vehiclePricing || {}) as Record<string, number | undefined>;
                  return Object.keys(vp).filter(k => vp[k] && vp[k]! > 0);
                }).filter((v, i, arr) => arr.indexOf(v) === i).sort((a, b) => {
                  const order: Record<string, number> = { sedan: 1, cuv: 2, suv: 3, van: 4, motorcycle: 5, caravan: 6, boat: 7 };
                  return (order[a] || 99) - (order[b] || 99);
                }).map((vtype) => (
                  <button
                    key={vtype}
                    type="button"
                    onClick={() => {
                      setSelectedVehicleType(vtype);
                      setSelectedServices([]);
                    }}
                    className={`rounded-lg border-2 px-4 py-2 text-sm font-medium transition-all ${
                      selectedVehicleType === vtype
                        ? "border-primary bg-primary/10 text-white"
                        : "border-border/60 hover:border-primary/40"
                    }`}
                  >
                    {t(`booking.${vtype}`)}
                  </button>
                ))}
              </div>
            </Card>

            {/* Step 2: Services (only after vehicle type selected) */}
            {selectedVehicleType && (
              <Card className="rounded-xl border border-border/50 p-6 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <Label>{t("giftPage.selectServices")}</Label>
                  {selectedServices.length > 0 && (
                    <span className="text-sm font-semibold text-primary">
                      {packageTotal.toFixed(3)} {currency}
                    </span>
                  )}
                </div>
                {servicesLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {services.filter(s => {
                      if (s.isAddOn) return false;
                      const vp = (s.vehiclePricing || {}) as Record<string, number | undefined>;
                      return vp[selectedVehicleType] && vp[selectedVehicleType]! > 0;
                    }).map((s) => {
                      const selection = selectedServices.find((sel) => sel.serviceId === s.id);
                      const isSelected = !!selection;
                      const vp = (s.vehiclePricing || {}) as Record<string, number | undefined>;
                      const price = vp[selectedVehicleType] ?? s.price;
                      const features = (isRtl ? s.descriptionAr : s.descriptionEn || "").split(/[،,]/).map((f: string) => f.trim()).filter((f: string) => f.length > 0);
                      const isExpanded = expandedService === s.id;

                      return (
                        <div key={s.id} className={`rounded-xl border-2 overflow-hidden transition-all ${isSelected ? "border-primary" : "border-border/60"}`}>
                          {/* Main row — entire card clickable */}
                          <div
                            role="button"
                            onClick={() => {
                              setSelectedServices((prev) =>
                                isSelected
                                  ? prev.filter((sel) => sel.serviceId !== s.id)
                                  : [...prev, { serviceId: s.id, vehicleType: selectedVehicleType }]
                              );
                            }}
                            className={`flex gap-3 p-3 cursor-pointer ${isSelected ? "bg-primary/5" : "hover:bg-muted/30"}`}
                          >
                            {s.imageUrl && (
                              <img src={s.imageUrl} alt={formatServiceName(s)} className="w-14 h-14 rounded-lg object-cover shrink-0" loading="lazy" />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${
                                    isSelected ? "border-primary bg-primary" : "border-border"
                                  }`}>
                                    {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                                  </div>
                                  <div className="min-w-0 text-start">
                                    <p className="text-sm font-medium truncate">{formatServiceName(s)}</p>
                                    {s.duration && <p className="text-xs text-muted-foreground">{s.duration} min</p>}
                                  </div>
                                </div>
                                <span className="text-sm font-semibold shrink-0">{price.toFixed(3)} {currency}</span>
                              </div>
                              {/* Short features */}
                              {features.length > 0 && !isExpanded && (
                                <p className="text-[10px] text-muted-foreground mt-1 line-clamp-1">
                                  {features.join("، ")}
                                </p>
                              )}
                              {/* Details toggle */}
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setExpandedService(isExpanded ? null : s.id); }}
                                className="text-xs text-primary hover:underline mt-1"
                              >
                                {isExpanded ? t("giftPage.hideDetails") : t("giftPage.showDetails")}
                              </button>
                            </div>
                          </div>

                          {/* Expanded details */}
                          {isExpanded && (
                            <div className="px-3 pb-3 pt-1 border-t border-border/40">
                              {features.length > 0 && (
                                <ul className="space-y-1">
                                  {features.map((f: string, i: number) => (
                                    <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                                      <Check className="h-3 w-3 text-primary shrink-0 mt-0.5" />
                                      {f}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {selectedServices.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {selectedServices.length} {selectedServices.length === 1 ? "service" : "services"} selected
                  </p>
                )}
              </Card>
            )}
          </>
        )}

        {/* Delivery method: how the gift code reaches the recipient */}
        <Card className="rounded-xl border border-border/50 p-6 mb-6">
          <Label className="mb-2 block text-sm">
            {t("giftPage.sendMethod")}
          </Label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => {
                setDeliveryMethod("share");
                // Clear recipient details — they're only used for SMS.
                setRecipientName("");
                setRecipientPhone("");
              }}
              className={cn(
                "flex flex-col items-center justify-center gap-1 p-3 rounded-xl border-2 transition-all",
                deliveryMethod === "share"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50",
              )}
            >
              <Share2 className="h-5 w-5 text-primary" />
              <span className="text-xs font-medium">{t("giftPage.sendManual")}</span>
            </button>
            <button
              type="button"
              onClick={() => setDeliveryMethod("sms")}
              className={cn(
                "flex flex-col items-center justify-center gap-1 p-3 rounded-xl border-2 transition-all",
                deliveryMethod === "sms"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50",
              )}
            >
              <MessageSquare className="h-5 w-5 text-primary" />
              <span className="text-xs font-medium">{t("giftPage.sendSms")}</span>
            </button>
          </div>
          {deliveryMethod === "sms" && (
            <p className="text-xs text-muted-foreground mt-2">
              {t("giftPage.smsNote")}
            </p>
          )}

          {/* Recipient name + phone — only relevant for SMS delivery */}
          {deliveryMethod === "sms" && (
            <div className="mt-4 space-y-4">
              <div>
                <Label htmlFor="recipient-name" className="mb-2 block">
                  {t("giftPage.recipientName")}
                </Label>
                <Input
                  id="recipient-name"
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder={t("giftPage.recipientNamePlaceholder")}
                  maxLength={50}
                  className="h-12"
                />
              </div>
              <div>
                <Label htmlFor="recipient-phone" className="mb-2 block">
                  {t("giftPage.recipientPhone")}
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none">
                    +965
                  </span>
                  <Input
                    id="recipient-phone"
                    type="tel"
                    value={recipientPhone}
                    onChange={(e) => setRecipientPhone(normalizeKuwaitPhoneInput(e.target.value))}
                    placeholder={t("giftPage.recipientPhonePlaceholder")}
                    maxLength={8}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="h-12 pl-14 text-left"
                    dir="ltr"
                    data-testid="input-recipient-phone"
                  />
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Payment method */}
        <Card className="rounded-xl border border-border/50 p-6 mb-6">
          <Label className="mb-3 block">{t("giftPage.paymentMethod")}</Label>
          <div className="grid grid-cols-2 gap-3">
            {/* Wallet */}
            <button
              type="button"
              onClick={() => setPaymentMethod("wallet")}
              className={cn(
                "flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all gap-2",
                paymentMethod === "wallet"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50",
              )}
            >
              <WalletIcon className="w-7 h-7 text-primary" />
              <span className="text-xs font-medium">{t("giftPage.payWithWallet")}</span>
              <span className="text-[10px] text-muted-foreground">{walletBalance.toFixed(3)} {currency}</span>
            </button>

            {/* KNET */}
            <button
              type="button"
              onClick={() => setPaymentMethod("knet")}
              className={cn(
                "flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all",
                paymentMethod === "knet"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50",
              )}
            >
              <img src={knetLogo} alt="KNET" className="w-12 h-12 object-contain rounded-lg" />
            </button>

            {/* Credit Card */}
            <button
              type="button"
              onClick={() => setPaymentMethod("credit_card")}
              className={cn(
                "flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all",
                paymentMethod === "credit_card"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50",
              )}
            >
              <img src={creditCardLogos} alt="Visa / Mastercard" className="w-28 h-12 object-contain rounded-lg" />
            </button>

            {/* Apple Pay */}
            <button
              type="button"
              onClick={() => {
                setPaymentMethod("apple_pay");
                setShowApplePayDialog(true);
              }}
              className={cn(
                "flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all",
                paymentMethod === "apple_pay"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50",
              )}
            >
              <img
                src={theme === "dark" ? applePayLogoDark : applePayLogoLight}
                alt="Apple Pay"
                className="h-12 object-contain"
              />
            </button>
          </div>

          {insufficientBalance && paymentMethod === "wallet" && (
            <p className="text-sm text-destructive mt-2">
              {t("giftPage.insufficientBalance")}
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-2">{t("booking.payment.secureNote")}</p>
        </Card>

        {/* Apple Pay KNET-only Warning Dialog */}
        <AlertDialog open={showApplePayDialog} onOpenChange={setShowApplePayDialog}>
          <AlertDialogContent className="max-w-md p-0 gap-0 overflow-hidden rounded-lg">
            <div className="p-6 sm:p-8 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-500/10 ring-8 ring-yellow-500/5">
                <AlertTriangle className="h-8 w-8 text-yellow-600 dark:text-yellow-500" />
              </div>
              <AlertDialogHeader className="space-y-2 text-center">
                <AlertDialogTitle className="text-lg font-bold">
                  {t("booking.payment.applePayKnetOnlyTitle")}
                </AlertDialogTitle>
                <AlertDialogDescription className="text-sm leading-relaxed">
                  {t("booking.payment.applePayKnetOnly")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="sm:justify-center mt-6">
                <AlertDialogAction
                  className="w-full sm:w-auto min-w-[140px] bg-yellow-600 hover:bg-yellow-700 focus-visible:ring-yellow-500"
                  onClick={() => setShowApplePayDialog(false)}
                >
                  {t("booking.ok")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </div>
          </AlertDialogContent>
        </AlertDialog>

        {/* Buy button */}
        <Button
          size="lg"
          className="w-full"
          onClick={handlePurchase}
          disabled={
            purchaseMutation.isPending ||
            (giftType === "package" && selectedServices.length === 0) ||
            insufficientBalance
          }
        >
          {purchaseMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("giftPage.validating")}
            </>
          ) : (
            <>
              <Gift className="h-4 w-4" />
              {t("giftPage.buyNow")}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
